/**
 * Customer-facing workflow coverage for the living Task document.
 * Keep in sync with server/src/services/workBriefPlanningState.ts.
 */

function splitPlanningText({ requestText = "", questions = [] } = {}) {
  const answered = [];
  const open = [];
  for (const question of questions) {
    const blob = [question.text, question.answer, question.resolution?.answerText, question.resolution?.reason]
      .filter(Boolean)
      .join(" ");
    if (question.status === "answered" || question.status === "not_required") {
      answered.push(blob);
    } else {
      open.push(blob);
    }
  }
  return { request: String(requestText || ""), answers: answered.join("\n"), open: open.join("\n") };
}

function coverageFrom(parts, decided, protectedByRequest) {
  if (decided.test(parts.answers)) return "decided";
  if (protectedByRequest && protectedByRequest.test(parts.request)) return "protected";
  if (decided.test(parts.request)) return "assumed";
  return "open";
}

const COVERAGE_LABELS = {
  protected: "Protected",
  decided: "Decided",
  assumed: "From your request",
  open: "Still open",
};

function fastifyWorkflows(parts) {
  return [
    {
      id: "valid-lowercase",
      title: "Valid lowercase keys keep working",
      summary: "Existing valid schema behavior must still validate the same way.",
      coverage: coverageFrom(
        parts,
        /keep (?:their )?current behavior|protected lowercase|valid lowercase content-type/i,
        /existing valid schema behavior must continue working/i
      ),
    },
    {
      id: "mixed-case",
      title: "Mixed-case keys validate consistently",
      summary: "Looking up the same media type with different capitalization must not skip validation.",
      coverage: coverageFrom(
        parts,
        /mixed-case|case-insensitive|regardless of capitali[sz]ation/i
      ),
    },
    {
      id: "duplicate-keys",
      title: "Duplicate case-equivalent keys fail clearly",
      summary: "Two keys that differ only by capitalization cannot silently overwrite each other.",
      coverage: coverageFrom(
        parts,
        /differ only by capitali[sz]ation|duplicate (?:case[- ]equivalent )?keys/i
      ),
    },
    {
      id: "invalid-media-type",
      title: "Invalid media types fail clearly",
      summary: "A schema key that is not a valid media type must be rejected, not skipped.",
      coverage: coverageFrom(
        parts,
        /not a valid media type|invalid media type|malformed media type/i
      ),
    },
    {
      id: "when-to-fail",
      title: "Invalid configuration fails at a known moment",
      summary: "Operators need to know whether this fails at startup or when a request is handled.",
      coverage: coverageFrom(parts, /startup or registration|when a request is handled/i),
    },
  ];
}

function accessWorkflows(parts) {
  return [
    {
      id: "allowed-access",
      title: "Permitted people can open the protected surface",
      summary: "Who may access what has to be explicit before engineering estimates authorization work.",
      coverage: coverageFrom(parts, /who (?:may|can) access|which permission|which roles/i),
    },
    {
      id: "denied-access",
      title: "Denied or revoked access leaks nothing",
      summary: "Unauthorized users receive a denial with no protected content.",
      coverage: coverageFrom(parts, /unauthori[sz]ed|denied or revoked|forbidden/i),
    },
  ];
}

function waitlistWorkflows(parts) {
  return [
    {
      id: "first-signup",
      title: "First valid signup succeeds",
      summary: "A new email is stored and the visitor sees a success state.",
      coverage: coverageFrom(parts, /waitlist|capture emails|signed up/i),
    },
    {
      id: "duplicate-signup",
      title: "Duplicate signup is explicit",
      summary: "Submitting the same email again has a defined result.",
      coverage: coverageFrom(parts, /duplicate email|already signed up/i),
    },
  ];
}

export function derivePlanningWorkflows(input = {}) {
  const request = String(input.requestText || "");
  const parts = splitPlanningText(input);
  if (/\bcontent[- ]type\b|\bschema keys?\b|\bcapitali[sz]ation\b|\bmedia types?\b/i.test(request)) {
    return fastifyWorkflows(parts);
  }
  if (/\brole[- ]based|\brbac\b|\baccess control|\bpermission|\bshare access\b/i.test(request)) {
    return accessWorkflows(parts);
  }
  if (/\bwaitlist\b|\bcapture emails\b|\bemail list\b/i.test(request)) {
    return waitlistWorkflows(parts);
  }
  return [];
}

export function planningCoverageLabel(coverage) {
  return COVERAGE_LABELS[coverage] || "Still open";
}
